// src/pages/Dashboard.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  listenToRideRequests, listenToDeliveryRequests,
  acceptRide, acceptDeliveryOrder, getRiderProfile, listenToRiderProfile, getRiderJobStats,
  saveFcmToken, updateRiderOnlineStatus, listenToForegroundMessages, fetchAvailableDeliveries,
  getOrderById,
} from '../services/firebase';
import { attachReconnectingListener, requestLiveReconnect } from '../utils/liveListener';
import { playAlertSound, unlockAudio } from '../utils/alertSound';
import { useRider } from '../context/RiderContext';
import { FEATURES } from '../config/features';
import { useLanguage } from '../i18n/LanguageContext';
import NotificationPrompt from '../components/NotificationPrompt';
import FirstTimeTour from '../components/FirstTimeTour';
import { hasSeenTour, markTourSeen } from '../utils/tour';

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    rider, isOnline, setIsOnline, setCurrentRide, setCurrentOrder,
    jobStats, setJobStats, activeOrder, riderRating, getRiderCoords,
    primeLocation, locationIssueKey,
  } = useRider();
  const [riderProfile, setRiderProfile] = useState(null);
  const [mode, setMode] = useState(FEATURES.RIDES_ENABLED ? 'rides' : 'delivery');
  const [incomingRide, setIncomingRide] = useState(null);
  const [incomingOrder, setIncomingOrder] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);
  const rideListener = useRef(null);
  const deliveryListener = useRef(null);
  const acceptTimeout = useRef(null);
  const lastAlertOrderId = useRef(null);
  const incomingOrderRef = useRef(null);
  const autoResumedRef = useRef(false);
  const onlineSyncedRef = useRef(false);

  const ratingDisplay = riderRating != null
    ? t('dashboard.ratingValue', { rating: riderRating })
    : t('dashboard.noRating');

  useEffect(() => {
    incomingOrderRef.current = incomingOrder;
  }, [incomingOrder]);

  useEffect(() => {
    if (!activeOrder || autoResumedRef.current) return;
    autoResumedRef.current = true;
    setCurrentOrder(activeOrder);
    navigate('/active-delivery');
  }, [activeOrder, setCurrentOrder, navigate]);

  const showDeliveryRequest = useCallback((order) => {
    if (!order || !['confirmed', 'ready'].includes(order.status)) return;
    lastAlertOrderId.current = order.id;
    playAlertSound();
    clearTimeout(acceptTimeout.current);
    acceptTimeout.current = setTimeout(() => setIncomingOrder(null), 30000);
    setIncomingOrder(order);
  }, []);

  const onDeliveryRequests = useCallback((orders) => {
    const ready = orders
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    if (!ready.length) {
      setIncomingOrder(null);
      return;
    }
    const next = ready[0];
    if (lastAlertOrderId.current !== next.id || !incomingOrderRef.current) {
      showDeliveryRequest(next);
    } else {
      setIncomingOrder((prev) => prev || next);
    }
  }, [showDeliveryRequest]);

  const approvedToastShown = useRef(false);

  useEffect(() => {
    if (!rider) return;
    const unsubProfile = listenToRiderProfile(rider.uid, (p) => {
      setProfileLoading(false);
      if (!p || !p.workType) {
        navigate('/onboard');
        return;
      }
      setRiderProfile((prev) => {
        if (prev && !prev.isApproved && p.isApproved && !approvedToastShown.current) {
          approvedToastShown.current = true;
          toast.success(t('dashboard.approveToast'));
        }
        return p;
      });
      setMode(FEATURES.RIDES_ENABLED ? (p.workType || 'rides') : 'delivery');
      if (!p.isApproved || p.isBlocked) {
        setIsOnline(false);
      } else if (!onlineSyncedRef.current) {
        onlineSyncedRef.current = true;
        if (p.isOnline) {
          setIsOnline(true);
          saveFcmToken(rider.uid);
          const workMode = FEATURES.RIDES_ENABLED ? (p.workType || 'rides') : 'delivery';
          updateRiderOnlineStatus(rider.uid, true, workMode).catch(() => {});
        }
      }
    });
    saveFcmToken(rider.uid);
    const unsubStats = getRiderJobStats(rider.uid, setJobStats);
    return () => {
      unsubProfile();
      unsubStats();
    };
  }, [rider, navigate, setIsOnline, setJobStats]);

  useEffect(() => {
    if (rider?.uid && !profileLoading && riderProfile?.workType && !hasSeenTour(rider.uid)) {
      setTourOpen(true);
    }
  }, [rider?.uid, profileLoading, riderProfile?.workType]);

  const closeTour = () => {
    if (rider?.uid) markTourSeen(rider.uid);
    setTourOpen(false);
  };

  useEffect(() => {
    if (!rider) return;
    let unsubFcm = () => {};
    listenToForegroundMessages((payload) => {
      const title = payload.notification?.title || 'Delivery update';
      const body = payload.notification?.body || '';
      toast(`${title}${body ? ` — ${body}` : ''}`, { duration: 6000 });
      if (payload.data?.type === 'delivery_request' && isOnline) {
        playAlertSound();
        const orderId = payload.data?.orderId;
        if (orderId) {
          fetchAvailableDeliveries(getRiderCoords()).then((orders) => {
            const order = orders.find((o) => o.id === orderId);
            if (order && ['confirmed', 'ready'].includes(order?.status)) {
              showDeliveryRequest(order);
            }
          });
        }
      }
      requestLiveReconnect();
    }).then((u) => { unsubFcm = u; });
    return () => unsubFcm();
  }, [rider, isOnline, showDeliveryRequest, getRiderCoords]);

  useEffect(() => {
    if (!isOnline || !rider) return;
    const activeMode = FEATURES.RIDES_ENABLED ? mode : 'delivery';
    if (activeMode !== 'delivery' && activeMode !== 'both') return;

    return attachReconnectingListener(() => {
      if (deliveryListener.current) {
        deliveryListener.current();
      }
      deliveryListener.current = listenToDeliveryRequests(onDeliveryRequests, getRiderCoords);
      return () => {
        if (deliveryListener.current) {
          deliveryListener.current();
          deliveryListener.current = null;
        }
      };
    });
  }, [isOnline, rider, mode, onDeliveryRequests, getRiderCoords]);

  useEffect(() => {
    if (!isOnline || !rider) return;
    const poll = setInterval(() => requestLiveReconnect(), 25000);
    return () => clearInterval(poll);
  }, [isOnline, rider]);

  const toggleOnline = async () => {
    const goingOnline = !isOnline;
    const approved = riderProfile?.isApproved === true && riderProfile?.isBlocked !== true;

    if (goingOnline && !approved) {
      toast.error(riderProfile?.isBlocked
        ? t('dashboard.blocked')
        : t('dashboard.adminApproval'));
      return;
    }

    if (goingOnline) {
      unlockAudio();
      const loc = await primeLocation();
      if (!loc.ok) {
        toast(t(`dashboard.${locationIssueKey(loc.reason)}`), { duration: 6000 });
      }
      onlineSyncedRef.current = true;
      setIsOnline(true);
      try {
        await updateRiderOnlineStatus(rider.uid, true, FEATURES.RIDES_ENABLED ? mode : 'delivery');
        await saveFcmToken(rider.uid);
        toast.success(t('dashboard.youAreOnline').replace('!', ' 🟢'));
        const activeMode = FEATURES.RIDES_ENABLED ? mode : 'delivery';
        if (FEATURES.RIDES_ENABLED && (activeMode === 'rides' || activeMode === 'both')) {
          rideListener.current = listenToRideRequests((requests) => {
            if (requests.length > 0) {
              setIncomingRide(prev => {
                if (prev) return prev;
                playAlertSound();
                clearTimeout(acceptTimeout.current);
                acceptTimeout.current = setTimeout(() => setIncomingRide(null), 30000);
                return requests[0];
              });
            } else {
              setIncomingRide(null);
            }
          });
        }
      } catch (e) {
        setIsOnline(false);
        toast.error(e?.code === 'permission-denied'
          ? 'Cannot go online yet — waiting for admin approval.'
          : 'Could not go online. Try again.');
        return;
      }
    } else {
      onlineSyncedRef.current = true;
      setIsOnline(false);
      toast(t('dashboard.offline') + ' ⚫');
      try {
        await updateRiderOnlineStatus(rider.uid, false, FEATURES.RIDES_ENABLED ? mode : 'delivery');
      } catch (e) {
        console.warn('offline status:', e?.message);
      }
      if (rideListener.current) { rideListener.current(); rideListener.current = null; }
      setIncomingRide(null);
      setIncomingOrder(null);
    }
  };
  useEffect(() => () => {
    clearTimeout(acceptTimeout.current);
    if (rideListener.current) rideListener.current();
  }, []);
  const handleAcceptRide = async (ride) => {
    setAccepting(true);
    clearTimeout(acceptTimeout.current);
    try {
      const profile = await getRiderProfile(rider.uid);
      if (!profile) { toast.error('Profile not found. Please complete setup.'); return; }
      await acceptRide(ride.id, {
        uid: rider.uid,
        name: profile.name,
        phone: profile.phone,
        vehicle: profile.vehicle,
        vehicleNumber: profile.vehicleNumber,
      });
      setCurrentRide({ ...ride, status: 'driver_assigned' });
      setIncomingRide(null);
      toast.success('Ride accepted! 🛺');
      navigate('/active-ride');
    } catch (e) {
      console.error(e);
      toast.error('Could not accept ride. Try again.');
    } finally { setAccepting(false); }
  };
  const handleAcceptOrder = async (order) => {
    setAccepting(true);
    clearTimeout(acceptTimeout.current);
    try {
      const profile = await getRiderProfile(rider.uid);
      if (!profile) { toast.error('Profile not found.'); return; }
      await acceptDeliveryOrder(order.id, {
        uid: rider.uid, name: profile.name, phone: profile.phone,
      });
      const fullOrder = await getOrderById(order.id);
      setCurrentOrder(fullOrder || { ...order, id: order.id, status: 'confirmed' });
      setIncomingOrder(null);
      toast.success(t('dashboard.deliveryAccepted'));
      navigate('/active-delivery');
    } catch (e) {
      console.error(e);
      if (e.message === 'ORDER_ALREADY_ASSIGNED' || e.code === 'permission-denied') {
        toast.error(t('dashboard.orderTaken'));
        setIncomingOrder(null);
      } else if (e.message === 'ORDER_NOT_READY' || e.message === 'ORDER_NOT_FOUND') {
        toast.error(t('dashboard.orderNotAvailable'));
        setIncomingOrder(null);
      } else {
        toast.error(t('dashboard.acceptFailed'));
      }
    } finally { setAccepting(false); }
  };
  if (profileLoading) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', minHeight: '100vh', background: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 48 }}>📦</div>
        <div className="spinner" />
      </div>
    );
  }

  const canGoOnline = riderProfile?.isApproved === true && riderProfile?.isBlocked !== true;

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-logo">My<span>Townly</span></div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? 'var(--green)' : 'var(--gray)', animation: isOnline ? 'pulse 2s infinite' : 'none' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: isOnline ? 'var(--green)' : 'var(--gray)' }}>{isOnline ? t('dashboard.online') : t('dashboard.offline')}</span>
        </div>
      </div>
      <div className="page-content">
        <NotificationPrompt />
        {activeOrder && (
          <button
            type="button"
            className="continue-delivery-banner"
            onClick={() => {
              setCurrentOrder(activeOrder);
              navigate('/active-delivery');
            }}
          >
            <span className="continue-delivery-banner__icon" aria-hidden>📦</span>
            <span className="continue-delivery-banner__text">
              <span className="continue-delivery-banner__title">{t('dashboard.continueDelivery')}</span>
              <span className="continue-delivery-banner__sub">
                {t('dashboard.continueDeliverySub', {
                  status: activeOrder.status === 'picked_up'
                    ? t('dashboard.statusPickedUp')
                    : t('dashboard.statusReady'),
                })}
              </span>
            </span>
            <span className="continue-delivery-banner__arrow" aria-hidden>→</span>
          </button>
        )}
        {/* ── MODE SELECTOR ── */}
        {FEATURES.RIDES_ENABLED && (
        <div style={{ margin: '16px 16px 0', background: 'var(--card)', borderRadius: 14, padding: 4, display: 'flex', border: '1px solid var(--border)' }}>
          {[
            { id: 'rides', label: '🛺 Rides' },
            { id: 'delivery', label: '📦 Delivery' },
            { id: 'both', label: '🔄 Both' },
          ].map(m => (
            <button key={m.id}
              onClick={() => {
                if (!isOnline && riderProfile?.workType === 'both') setMode(m.id);
              }}
              style={{
                flex: 1, padding: '9px 4px', borderRadius: 10, border: 'none',
                cursor: riderProfile?.workType !== 'both' && m.id !== riderProfile?.workType ? 'not-allowed' : isOnline ? 'not-allowed' : 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 700,
                background: mode === m.id ? 'var(--green)' : 'transparent',
                color: mode === m.id ? 'var(--black)' : 'var(--gray2)',
                transition: 'all 0.15s',
                opacity: (isOnline && mode !== m.id) || (riderProfile?.workType !== 'both' && m.id !== riderProfile?.workType) ? 0.3 : 1,
              }}>
              {m.label}
            </button>
          ))}
        </div>
        )}
        {FEATURES.RIDES_ENABLED && isOnline && (
          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--gray)', marginTop: 6 }}>
            Go offline to change mode
          </div>
        )}
        {/* ── ONLINE TOGGLE HERO ── */}
        <div data-tour="online" style={{ background: isOnline ? 'linear-gradient(135deg, rgba(0,214,143,0.12), rgba(0,168,107,0.06))' : 'var(--dark)', margin: 16, borderRadius: 20, padding: 24, border: `1px solid ${isOnline ? 'rgba(0,214,143,0.3)' : 'var(--border)'}`, textAlign: 'center', transition: 'all 0.4s', opacity: canGoOnline || isOnline ? 1 : 0.55 }}>
          <div
            onClick={canGoOnline || isOnline ? toggleOnline : () => toast.error('Approve pending — contact MyTownly admin')}
            style={{
              width: 88, height: 88, borderRadius: '50%', margin: '0 auto 16px',
              cursor: canGoOnline || isOnline ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, transition: 'all 0.3s',
              background: isOnline ? 'var(--green)' : 'var(--card)',
              boxShadow: isOnline ? '0 0 40px rgba(0,214,143,0.4)' : 'none',
              border: `3px solid ${isOnline ? 'var(--green)' : 'var(--border)'}`,
            }}>
            {isOnline ? '🟢' : '⚫'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: isOnline ? 'var(--green)' : 'var(--text)' }}>
            {isOnline ? t('dashboard.youAreOnline') : t('dashboard.youAreOffline')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>
            {!canGoOnline && !isOnline
              ? t('dashboard.adminApproval')
              : isOnline
              ? (FEATURES.RIDES_ENABLED
                ? (mode === 'rides' ? 'Waiting for ride requests...'
                  : mode === 'delivery' ? t('dashboard.waitingDelivery')
                  : 'Waiting for rides & deliveries...')
                : t('dashboard.waitingDelivery'))
              : t('dashboard.tapOnline')}
          </div>
          {!isOnline && (
            <button
              className="btn btn-green"
              onClick={toggleOnline}
              disabled={!canGoOnline}
              style={{ marginTop: 16, maxWidth: 200, opacity: canGoOnline ? 1 : 0.45, cursor: canGoOnline ? 'pointer' : 'not-allowed' }}
            >
              {canGoOnline ? t('dashboard.goOnline') : t('dashboard.approvalPending')}
            </button>
          )}
        </div>
        {/* Today's stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '0 16px', marginBottom: 16 }}>
          {[
            { label: t('dashboard.todayJobs'), value: jobStats.todayTrips || 0, icon: '📦', color: 'var(--orange)' },
            { label: t('dashboard.totalJobs'), value: jobStats.totalTrips || 0, icon: '✅', color: 'var(--green)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {/* All time stats */}
        <div className="card" style={{ margin: '0 16px', padding: 16, display: 'flex', justifyContent: 'space-around', marginBottom: 12 }}>
          {[
            { label: t('dashboard.rating'), value: ratingDisplay },
            { label: t('dashboard.status'), value: riderProfile?.isApproved ? t('dashboard.approved') : t('dashboard.pending') },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {/* Rider profile card */}
        {riderProfile && (
          <div className="card" style={{ margin: '0 16px', padding: 16, display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--green-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, border: '2px solid var(--green)', flexShrink: 0 }}>👨</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{riderProfile.name}</div>
              <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>{riderProfile.vehicle}</div>
              <div style={{ background: 'var(--border)', color: 'var(--text)', fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 6, marginTop: 4, display: 'inline-block', letterSpacing: 1, fontFamily: 'DM Mono, monospace' }}>{riderProfile.vehicleNumber}</div>
            </div>
            <div style={{ fontSize: 12, color: riderProfile.isApproved ? 'var(--green)' : 'var(--orange)', fontWeight: 700 }}>
              {riderProfile.isApproved ? t('dashboard.approved') : t('dashboard.pending')}
            </div>
          </div>
        )}
        {/* Pending approval banner */}
        {riderProfile && !riderProfile.isApproved && (
          <div style={{ margin: '0 16px 12px', padding: 14, background: 'rgba(255,160,0,0.08)', borderRadius: 14, border: '1px solid rgba(255,160,0,0.25)', textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>⏳</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--orange)' }}>{t('dashboard.waitingApproval')}</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>{t('dashboard.waitingApprovalSub')}</div>
          </div>
        )}
        <div data-tour="tips" style={{ margin: '0 16px 80px', padding: 16, background: 'rgba(0,214,143,0.05)', borderRadius: 16, border: '1px solid rgba(0,214,143,0.15)' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--green)', marginBottom: 8 }}>💡 {t('dashboard.howItWorks')}</div>
          {[
            t('dashboard.tip1'),
            t('dashboard.tip2'),
            t('dashboard.tip3'),
          ].map(tip => (
            <div key={tip} style={{ fontSize: 12, color: 'var(--gray)', padding: '3px 0' }}>✓ {tip}</div>
          ))}
        </div>
      </div>
      {/* Bottom nav */}
      <div className="bottom-nav">
        {[
          { label: t('nav.home'), emoji: '🏠', path: '/', active: true },
          { label: t('nav.history'), emoji: '📋', path: '/history' },
          { label: t('nav.profile'), emoji: '👤', path: '/profile' },
        ].map(n => (
          <button key={n.label} className={`nav-item ${n.active ? 'active' : ''}`} data-tour={n.path === '/history' ? 'history' : undefined} onClick={() => navigate(n.path)}>
            <div className="nav-icon">{n.emoji}</div>
            <div className="nav-label">{n.label}</div>
          </button>
        ))}
      </div>
      {/* ── INCOMING RIDE POPUP ── */}
      {FEATURES.RIDES_ENABLED && incomingRide && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', animation: 'fadeIn 0.2s ease' }}>
          <div style={{ background: 'var(--card)', borderRadius: '24px 24px 0 0', padding: 24, width: '100%', maxWidth: 420, border: '1px solid var(--border)', animation: 'slideUp 0.3s ease' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', letterSpacing: 1, marginBottom: 8 }}>🛺 NEW RIDE REQUEST</div>
              <div style={{ width: 60, height: 60, borderRadius: '50%', border: '3px solid var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: 28, animation: 'pulse 1s infinite' }}>🛺</div>
            </div>
            <div style={{ background: 'var(--dark)', borderRadius: 14, padding: 14, marginBottom: 14, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)', flexShrink: 0, marginTop: 4 }} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--gray)' }}>PICKUP</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{incomingRide.pickup?.address || 'Customer Location'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--orange)', flexShrink: 0, marginTop: 4 }} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--gray)' }}>DROP</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{incomingRide.destination?.name || 'Destination'}</div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {[
                { icon: '💰', label: 'Your cut', value: `₹${Math.round((incomingRide.fare || 0) * 0.85)}` },
                { icon: '📏', label: 'Distance', value: incomingRide.distance || '—' },
                { icon: '⏱', label: 'Duration', value: incomingRide.duration || '—' },
              ].map(s => (
                <div key={s.label} style={{ flex: 1, background: 'var(--dark)', borderRadius: 10, padding: '10px 8px', textAlign: 'center', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 16, marginBottom: 2 }}>{s.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 1 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => { clearTimeout(acceptTimeout.current); setIncomingRide(null); toast('Ride skipped'); }}>✕ Skip</button>
              <button className="btn btn-green" style={{ flex: 2 }} onClick={() => handleAcceptRide(incomingRide)} disabled={accepting}>
                {accepting ? <span className="spinner" /> : '✓ Accept Ride'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── INCOMING DELIVERY POPUP ── */}
      {incomingOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', animation: 'fadeIn 0.2s ease' }}>
          <div style={{ background: 'var(--card)', borderRadius: '24px 24px 0 0', padding: 24, width: '100%', maxWidth: 420, border: '1px solid var(--border)', animation: 'slideUp 0.3s ease' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', letterSpacing: 1, marginBottom: 8 }}>📦 {t('dashboard.newDelivery')}</div>
              <div style={{ width: 60, height: 60, borderRadius: '50%', border: '3px solid var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: 28, animation: 'pulse 1s infinite' }}>📦</div>
            </div>
            <div style={{ background: 'var(--dark)', borderRadius: 14, padding: 14, marginBottom: 14, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)', flexShrink: 0, marginTop: 4 }} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--gray)' }}>PICKUP FROM</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{incomingOrder.shopName || 'Shop'}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray2)', marginTop: 4 }}>{incomingOrder.shopAddress || '—'}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray)' }}>
                Customer address shown after you accept
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {[
                { icon: '🛒', label: 'Items', value: incomingOrder.itemCount ?? incomingOrder.items?.length ?? '—' },
                { icon: '📏', label: t('dashboard.distanceDelivery'), value: incomingOrder.deliveryDistanceKm != null ? `${incomingOrder.deliveryDistanceKm} km` : '—' },
                { icon: '📍', label: t('dashboard.distanceToYou'), value: (incomingOrder.riderDistanceKm != null && incomingOrder.riderDistanceKm < 500)
                  ? `${incomingOrder.riderDistanceKm} km`
                  : '—' },
                { icon: '💵', label: 'Payment', value: incomingOrder.paymentMode === 'cash' ? 'Collect' : 'Paid' },
              ].map(s => (
                <div key={s.label} style={{ flex: 1, background: 'var(--dark)', borderRadius: 10, padding: '10px 8px', textAlign: 'center', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 16, marginBottom: 2 }}>{s.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 1 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => { clearTimeout(acceptTimeout.current); setIncomingOrder(null); toast(t('dashboard.orderSkipped')); }}>{t('dashboard.skip')}</button>
              <button className="btn btn-green" style={{ flex: 2 }} onClick={() => handleAcceptOrder(incomingOrder)} disabled={accepting}>
                {accepting ? <span className="spinner" /> : t('dashboard.acceptDelivery')}
              </button>
            </div>
          </div>
        </div>
      )}
      <FirstTimeTour open={tourOpen} onFinish={closeTour} />
    </div>
  );
}